<template>
    <div>
        <auth-container :page="page">
            <h2 class="mb-4">Program Performance</h2>

            <b-card bg-variant="light" class="mb-4">
                <h5>Customize Date Range</h5>
                <div class="d-flex align-items-center justify-content-start">
                    <div class="mr-2">
                        <b-form-select v-model="advancedFilter.date.type" :options="dateTypeOptions" @change="changeDateFilterType" style="max-width:300px"></b-form-select>
                    </div>

                    <div class="mr-2" v-if="advancedFilter.date.type === 'FY' || advancedFilter.date.type === 'CY'">
                        <b-form-input type="number" step="any" v-model="advancedFilter.date.year" @input="changeDateFilterType" style="max-width:200px"></b-form-input>
                    </div>

                    <div class="mr-2" v-if="advancedFilter.date.type === 'FY'">
                        <b-form-select v-model="advancedFilter.date.quarter" :options="fiscalQuarterOptions" @change="changeDateFilterType" style="max-width:300px"></b-form-select>
                    </div>

                    <div class="mr-2" v-if="advancedFilter.date.type === 'CY'">
                        <b-form-select v-model="advancedFilter.date.quarter" :options="calendarQuarterOptions" @change="changeDateFilterType" style="max-width:300px"></b-form-select>
                    </div>

                    <div class="mr-2" v-if="advancedFilter.date.type === 'Custom'">
                        <b-input-group>
                            <b-form-input type="date" v-model="advancedFilter.date.start" @input="checkDateAndGet"></b-form-input>

                            <b-input-group-append class="pointer">
                                <b-input-group-text @click="advancedFilter.date.start = null; get()">
                                    <font-awesome-icon :icon="['fas', 'times']" />
                                </b-input-group-text>
                            </b-input-group-append>
                        </b-input-group>
                    </div>
                    <div v-if="advancedFilter.date.type === 'Custom'">
                        <b-input-group>
                            <b-form-input type="date" v-model="advancedFilter.date.end" @input="checkDateAndGet"></b-form-input>

                            <b-input-group-append class="pointer">
                                <b-input-group-text @click="advancedFilter.date.end = null; get()">
                                    <font-awesome-icon :icon="['fas', 'times']" />
                                </b-input-group-text>
                            </b-input-group-append>
                        </b-input-group>
                    </div>
                </div>
            </b-card>

            <h3 class="mt-4">
                Showing:
                <span v-if="submittedDate.start === null && submittedDate.end === null">All Time</span>
                <span v-else>{{submittedDate.start | date('M/d/YYYY', 'Beginning')}} - {{submittedDate.end | date('M/d/YYYY', 'Present')}}</span>
            </h3>

            <b-row>
                <b-col lg="6">
                    <b-table-simple>
                        <b-thead class="bg-light">
                            <b-tr>
                                <b-th colspan="2"></b-th>
                                <b-th class="text-right">Count</b-th>
                                <b-th class="text-right">Amount</b-th>
                            </b-tr>
                        </b-thead>
                        <b-tbody>
                            <b-tr>
                                <b-th colspan="2">Applications</b-th>
                                <b-td class="text-right">{{ data.applications.count }}</b-td>
                                <b-td class="text-right">${{ data.applications.totalAmount | number(2) }}</b-td>
                            </b-tr>
                            <b-tr>
                                <b-th colspan="2">Invoices Submitted</b-th>
                                <b-td class="text-right">{{ data.invoicesSubmitted.count }}</b-td>
                                <b-td class="text-right">${{ data.invoicesSubmitted.totalAmount | number(2) }}</b-td>
                            </b-tr>
                            <b-tr>
                                <b-th colspan="2">Invoices Approved</b-th>
                                <b-td class="text-right">{{ data.invoicesApproved.count }}</b-td>
                                <b-td class="text-right">${{ data.invoicesApproved.totalAmount | number(2) }}</b-td>
                            </b-tr>
                            <b-tr>
                                <b-th rowspan="2">Reimbursements Received</b-th>
                                <b-td class="text-right">State/SLO</b-td>
                                <b-td class="text-right">{{ data.stateReimbursementsReceived.count }}</b-td>
                                <b-td class="text-right">${{ data.stateReimbursementsReceived.totalAmount | number(2) }}</b-td>
                            </b-tr>
                            <b-tr>
                                <b-td class="text-right">Federal</b-td>
                                <b-td class="text-right">{{ data.fedReimbursementsReceived.count }}</b-td>
                                <b-td class="text-right">${{ data.fedReimbursementsReceived.totalAmount | number(2) }}</b-td>
                            </b-tr>
                            <b-tr>
                                <b-th rowspan="2">Reimbursements Approved</b-th>
                                <b-td class="text-right">State/SLO</b-td>
                                <b-td class="text-right">{{ data.stateReimbursementsApproved.count }}</b-td>
                                <b-td class="text-right">${{ data.stateReimbursementsApproved.totalAmount | number(2) }}</b-td>
                            </b-tr>
                            <b-tr>
                                <b-td class="text-right">Federal</b-td>
                                <b-td class="text-right">{{ data.fedReimbursementsApproved.count }}</b-td>
                                <b-td class="text-right">${{ data.fedReimbursementsApproved.totalAmount | number(2) }}</b-td>
                            </b-tr>
                            <b-tr>
                                <b-th rowspan="2">Reimbursements Paid</b-th>
                                <b-td class="text-right">State/SLO</b-td>
                                <b-td class="text-right">{{ data.stateReimbursementsPaid.count }}</b-td>
                                <b-td class="text-right">${{ data.stateReimbursementsPaid.totalAmount | number(2) }}</b-td>
                            </b-tr>
                            <b-tr>
                                <b-td class="text-right">Paid</b-td>
                                <b-td class="text-right">{{ data.fedReimbursementsPaid.count }}</b-td>
                                <b-td class="text-right">${{ data.fedReimbursementsPaid.totalAmount | number(2) }}</b-td>
                            </b-tr>
                        </b-tbody>
                    </b-table-simple>
                </b-col>
            </b-row>

            

        </auth-container>
    </div>
</template>

<script>
    export default {
        name: 'SrReportProgramPerformance',
        data() {
            return {
                page: {
                    errors: [],
                    loading: true,
                    showLogin: false,
                },
                advancedFilter: {
                    date: {
                        start: null,
                        end: null,
                        type: 'All',
                        year: 0,
                        quarter: 'All'
                    },
                },
                submittedDate: {
                    start: null,
                    end: null
                },
                data: {
                    applications: {
                        count: 0,
                        totalAmount: 0,
                    },
                    invoicesSubmitted: {
                        count: 0,
                        totalAmount: 0,
                    },
                    invoicesApproved: {
                        count: 0,
                        totalAmount: 0,
                    },
                    stateReimbursementsReceived: {
                        count: 0,
                        totalAmount: 0,
                    },
                    stateReimbursementsApproved: {
                        count: 0,
                        totalAmount: 0,
                    },
                    stateReimbursementsPaid: {
                        count: 0,
                        totalAmount: 0,
                    },
                    fedReimbursementsReceived: {
                        count: 0,
                        totalAmount: 0,
                    },
                    fedReimbursementsApproved: {
                        count: 0,
                        totalAmount: 0,
                    },
                    fedReimbursementsPaid: {
                        count: 0,
                        totalAmount: 0,
                    },
                }
            }
        },
        async created() {
            await this.get();
        },
        watch: {
            '$route': 'get',
        },
        methods: {
            async get() {
                this.page.errors = [];
                this.page.loading = true;

                try {
                    let filterComplex = this.getFilterComplex();
                    const response = await this.$http.get(`srreports/programperformance${filterComplex}`, this.getTokenHeader());
                    this.log(response.data);
                    this.data = response.data;
                    this.submittedDate.start = this.advancedFilter.date.start;
                    this.submittedDate.end = this.advancedFilter.date.end;
                } catch (error) {
                    if (this.isApiUnauthorized(error)) this.page.showLogin = true;
                    else this.page.errors = this.logError(error);
                }

                this.page.loading = false;
            },
            getFilterComplex(startChar = '?') {
                let filterComplex = '';
                let filterValues = [];
                let stringFields = [];
                let twoPartFields = ['date'];

                for (let f of stringFields) {
                    if (!this.isNullOrEmpty(this.advancedFilter[f])) {
                        filterValues.push(`${f}:${this.advancedFilter[f]}`);
                    }
                }

                for (let f of twoPartFields) {
                    if (!this.isNullOrEmpty(this.advancedFilter[f].start) || !this.isNullOrEmpty(this.advancedFilter[f].end)) {
                        let s = this.advancedFilter[f].start;
                        let e = this.advancedFilter[f].end;
                        s = s === null ? 'any' : s;
                        e = e === null ? 'any' : e;
                        filterValues.push(`${f}:${s},${e}`);
                    }
                }

                if (filterValues.length > 0) filterComplex = startChar + 'filter=' + filterValues.join('|');
                return filterComplex;
            },
            async clearAdvancedFilter() {
                this.advancedFilter = {
                    date: {
                        start: null,
                        end: null,
                        type: 'All',
                        year: 0,
                        quarter: 'All'
                    },
                };

                await this.get();
            },
            async changeDateFilterType() {
                if (this.advancedFilter.date.year !== 0 && this.advancedFilter.date.year < 1000) return;

                switch (this.advancedFilter.date.type) {
                    case 'Custom':
                        return;
                    case 'FY':
                        if (this.advancedFilter.date.year === 0) this.advancedFilter.date.year = this.currentFiscalYear();
                        break;
                    case 'CY':
                        if (this.advancedFilter.date.year === 0) this.advancedFilter.date.year = this.currentFiscalYear();
                        break;
                    default:
                        this.advancedFilter.date.start = null;
                        this.advancedFilter.date.end = null;
                        await this.get();
                        return;
                }

                let d = this.getDatesFromQuarter(this.advancedFilter.date.year, this.advancedFilter.date.quarter, this.advancedFilter.date.type)
                this.advancedFilter.date.start = d[0];
                this.advancedFilter.date.end = d[1];
                await this.get();
            },
            async checkDateAndGet() {
                if (this.advancedFilter.date.start !== null && !this.isValidDate(this.advancedFilter.date.start)) return;
                if (this.advancedFilter.date.end !== null && !this.isValidDate(this.advancedFilter.date.end)) return;
                await this.get();
            }
        },
    }
</script>